/**
 * What a kitchen screen actually shows, worked out from what the API returns.
 *
 * The API answers per screen. Main receives the complete order, with work routed to an
 * added station marked as secondary. An added station receives the parts routed to it;
 * explicit secondary/both station modes combine the available display responses.
 */

export type DisplayMode = 'auto' | 'primary' | 'secondary' | 'both'

export type Alternative = {
  id: string
  prefix?: string | null
  name?: string | null
  alternativeGroupId?: string | null
  alternativeItemName?: string | null
  count?: number | null
  isChosen?: boolean | null
  isDefault?: boolean | null
  kitchenPresentation?: string | null
  productId?: string | null
}

export type LineItem = {
  preparedAtOtherStation?: string | null
  productId?: string | null
  id: string
  name?: string | null
  count?: number | null
  message?: string | null
  holdKitchenPrint?: boolean | null
  needsKitchenPrint?: boolean | null
  preparationStartedAt?: string | null
  preparationCompletedAt?: string | null
  preparationBy?: { id: string; name?: string | null } | null
  alternativeItems?: (Alternative | null)[] | null
}

export type LineItemSection = {
  id: string
  title?: string | null
  isSecondary?: boolean | null
  isCollapsed?: boolean | null
  items?: (LineItem | null)[] | null
}

export type Slip = {
  kitchenStationProgress?: {
    kitchenDisplayId: string
    state: 'Done' | 'Pending' | 'NotRequired'
  }[]
  id: string
  humanOrderCode?: string | null
  createdAt?: string | null
  deliveryTime?: string | null
  estimatedDeliveryTime?: string | null
  riderPickupTime?: string | null
  orderType?: string | null
  backgroundColor?: string | null
  message?: string | null
  needsConfirmation?: boolean | null
  readyForPickup?: boolean | null
  transactionChannel?: string | null
  tableName?: string | null
  till?: { id: string; name?: string | null } | null
  lineItemSections?: (LineItemSection | null)[] | null
}

export type Display = {
  id: string
  name?: string | null
  showsPrintButton?: boolean | null
  skipExplicitSlipPreparationStart?: boolean | null
  lateColorRules?: ({ threshold?: unknown; color?: string | null } | null)[] | null
  printerQueue?: { id: string; name?: string | null } | null
  slipSortOrder?: 'Created' | 'EstimatedDelivery' | null
  slips?: (Slip | null)[] | null
}

/**
 * "main" is the screen every restaurant starts with, and it is the kitchen rather than a
 * counter: left to itself it shows the whole order, with anything belonging to another
 * station marked. Adding a second screen must not empty the first one — the screen the
 * kitchen has always cooked from would go blank the day somebody adds a fryer station.
 *
 * A screen added afterwards is a station, and shows what it is there to make.
 *
 * Pong's arrangement, where the main screen shows only what stations send it, suits a
 * pass that cooks nothing itself. It is still available by choosing it outright.
 */
export function resolvedDisplayMode(mode: DisplayMode, kitchenDisplayId: string): Exclude<DisplayMode, 'auto'> {
  if (mode !== 'auto') return mode
  return kitchenDisplayId === 'main' ? 'both' : 'primary'
}

/**
 * Main's native slip already contains the complete order, including sections routed to
 * other stations. Added stations still need aggregation when explicitly configured to
 * show work beyond their own routing.
 */
export function shouldAggregateDisplays(kitchenDisplayId: string, mode: Exclude<DisplayMode, 'auto'>): boolean {
  return kitchenDisplayId !== 'main' && mode !== 'primary'
}

/**
 * Puts one order back together from the pieces each station returned. Sections are
 * matched by id, so the same section arriving from two displays is kept once.
 */
export function mergeSlips(displays: Display[]): Slip[] {
  const byId = new Map<string, Slip>()
  const sectionIds = new Map<string, Set<string>>()
  for (const display of displays) {
    for (const slip of display.slips ?? []) {
      if (!slip) continue
      const existing = byId.get(slip.id)
      if (!existing) {
        byId.set(slip.id, { ...slip, lineItemSections: [...(slip.lineItemSections ?? [])] })
        sectionIds.set(slip.id, new Set((slip.lineItemSections ?? []).filter((s) => s != null).map((s) => s.id)))
        continue
      }
      const seen = sectionIds.get(slip.id) ?? new Set<string>()
      for (const section of slip.lineItemSections ?? []) {
        if (!section || seen.has(section.id)) continue
        seen.add(section.id)
        existing.lineItemSections = [...(existing.lineItemSections ?? []), section]
      }
      // A slip is ready only when every station that holds a piece of it says so.
      if (slip.readyForPickup !== true) existing.readyForPickup = false
    }
  }
  return [...byId.values()]
}

/**
 * Selects the API slips backing a screen. Keeping Main on its own display preserves its
 * complete sections and its Main-qualified mutation IDs.
 */
export function slipsFromDisplays(displays: Display[], kitchenDisplayId: string, mode: Exclude<DisplayMode, 'auto'>): Slip[] {
  if (shouldAggregateDisplays(kitchenDisplayId, mode)) return mergeSlips(displays)
  const ownDisplay = displays.find((display) => display.id === kitchenDisplayId)
  return (ownDisplay?.slips ?? []).filter((slip) => slip != null)
}

/** The sections this screen is meant to show, given what it is for. */
export function visibleSections(slip: Slip, mode: Exclude<DisplayMode, 'auto'>): LineItemSection[] {
  const sections = (slip.lineItemSections ?? []).filter((section) => section != null)
  if (mode === 'both') return sections
  const wantSecondary = mode === 'secondary'
  return sections.filter((section) => Boolean(section.isSecondary) === wantSecondary)
}

/** A slip with nothing on it for this screen is not this screen's problem. */
export function slipsForDisplay(slips: Slip[], mode: Exclude<DisplayMode, 'auto'>): Slip[] {
  return slips.filter((slip) => visibleSections(slip, mode).length > 0)
}

export function items(section: LineItemSection): LineItem[] {
  return (section.items ?? []).filter((item) => item != null)
}
export function slipItems(slip: Slip, mode: Exclude<DisplayMode, 'auto'>): LineItem[] {
  return visibleSections(slip, mode).flatMap(items)
}

export type SlipState = 'waiting' | 'preparing' | 'done' | 'ready'
/** Preparation progress is ticket-level; shared line timestamps are not station completion. */
export function slipState(slip: Slip, _mode: Exclude<DisplayMode, 'auto'>): SlipState {
  if (slip.readyForPickup === true) return 'ready'
  const required = (slip.kitchenStationProgress ?? []).filter((station) => station.state !== 'NotRequired')
  if (!required.length) return 'waiting'
  if (required.every((station) => station.state === 'Done')) return 'done'
  return required.some((station) => station.state === 'Done') ? 'preparing' : 'waiting'
}

function parseTime(value?: string | null): number | null {
  if (!value) return null
  const time = Date.parse(value)
  return Number.isFinite(time) ? time : null
}

/** When this order is wanted, which is not always when it was rung up. */
export function dueAt(slip: Slip): number | null {
  return parseTime(slip.riderPickupTime) ?? parseTime(slip.deliveryTime) ?? parseTime(slip.estimatedDeliveryTime) ?? parseTime(slip.createdAt)
}

/**
 * An order wanted later than the threshold waits on its own tab, so the kitchen is not
 * looking at tomorrow's catering while lunch is running.
 */
export function isPreorder(slip: Slip, now: number, thresholdMinutes: number): boolean {
  const due = parseTime(slip.riderPickupTime) ?? parseTime(slip.deliveryTime)
  if (due === null) return false
  return due - now > thresholdMinutes * 60_000
}

export function ageSeconds(slip: Slip, now: number): number {
  const created = parseTime(slip.createdAt)
  return created === null ? 0 : Math.max(0, Math.round((now - created) / 1000))
}

/**
 * The colour an order turns as it gets old. Rules are given as a threshold and a colour;
 * the latest threshold the order has passed wins, so the rules can be listed in any order.
 */
export function lateColor(display: Display, ageSec: number): string | null {
  let best: { threshold: number; color: string } | null = null
  for (const rule of display.lateColorRules ?? []) {
    if (!rule?.color) continue
    const threshold = thresholdSeconds(rule.threshold)
    if (threshold === null || ageSec < threshold) continue
    if (!best || threshold > best.threshold) best = { threshold, color: rule.color }
  }
  return best?.color ?? null
}

/**
 * A threshold arrives as an ISO 8601 duration ("PT5M"), and older records carry plain
 * seconds. Both mean the same thing to a cook.
 */
export function thresholdSeconds(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value)
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(value)
  if (!match) return null
  const [, days, hours, minutes, seconds] = match
  if (!days && !hours && !minutes && !seconds) return null
  return Number(days ?? 0) * 86_400 + Number(hours ?? 0) * 3_600 + Number(minutes ?? 0) * 60 + Number(seconds ?? 0)
}

/**
 * Oldest first, because the order that has waited longest is the one to make next.
 * A slip already called ready sinks below the rest — it is finished work.
 */
export function sortSlips(slips: Slip[], mode: Exclude<DisplayMode, 'auto'>, sortOrder?: 'Created' | 'EstimatedDelivery'): Slip[] {
  return [...slips].sort((a, b) => {
    const readyA = slipState(a, mode) === 'ready'
    const readyB = slipState(b, mode) === 'ready'
    if (readyA !== readyB) return readyA ? 1 : -1
    const key = (slip: Slip) => (sortOrder === 'Created' ? parseTime(slip.createdAt) : dueAt(slip))
    return (key(a) ?? 0) - (key(b) ?? 0) || a.id.localeCompare(b.id)
  })
}

/** Only these channels, when the screen has been narrowed to some of them. */
export function filterChannels(slips: Slip[], channels: string[]): Slip[] {
  if (!channels.length) return slips
  const wanted = new Set(channels)
  return slips.filter((slip) => slip.transactionChannel && wanted.has(slip.transactionChannel))
}
